'use client';

import PageSection from '@c/listing/shared/PageSection';
import { useCreateListing } from 'app/create-listing/_context';
import React, { useState } from 'react';
import { MotoIcon } from './assets/icons/MotoIcon';
import { ClockIcon } from './assets/icons/ClockIcon';
import { TrendUpIcon } from './assets/icons/TrendUpIcon';
import { InstagramIcon } from './assets/icons/InstagramIcon';
import { EmailIcon } from './assets/icons/EmailIcon';
import FormLabel from '@c/forms/controls/FormLabel';
import { PlusCircleIcon } from '@heroicons/react/20/solid';
import { capitalize, formatCurrency, isMobile } from '@util/index';
import { getPaymentMethods } from '@util/firestore/payments';
import { useQuery } from '@tanstack/react-query';
import Select from '@ui/Select';
import AddPaymentMethodModal from '@c/modals/AddPaymentMethodModal';

export default function BikePackage() {
  const [openModal, setOpenModal] = useState('');
  const { form, selectedPaymentMethodId, setSelectedPaymentMethodId, product } =
    useCreateListing();
  const [packagePrice, setPackagePrice] = useState(
    product.bike_package === 2 ? 39.99 : product.bike_package === 1 ? 19.99 : 0
  );
  const { data: paymentMethodData, isLoading } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
  });
  const paymentOptions =
    paymentMethodData?.map((paymentMethod) => ({
      id: paymentMethod.id ?? '',
      label: `${capitalize(paymentMethod.brand)} - ${paymentMethod.last4}`,
      value: paymentMethod.id ?? '',
      name: paymentMethod.name,
    })) ?? [];
  return (
    <PageSection title="Choose bike listing package">
      <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-3">
        <PackageCard
          price={0}
          description="1st Gear"
          items={[
            {
              icon: <MotoIcon className="h-[3rem] w-[3rem]" />,
              text: 'Listing your bike for free',
            },
          ]}
          index={1}
          form={form}
          setPackagePrice={setPackagePrice}
        />

        <PackageCard
          price={19.99}
          description="2nd Gear"
          items={[
            {
              icon: <ClockIcon className="h-[3rem] w-[3rem]" />,
              text: 'Featured for 24 hrs',
            },
            {
              icon: <TrendUpIcon className="h-[3rem] w-[3rem]" />,
              text: '1x bump to top of search list',
            },
          ]}
          index={2}
          form={form}
          setPackagePrice={setPackagePrice}
        />

        <PackageCard
          price={39.99}
          description="3rd Gear"
          isPopular
          items={[
            {
              icon: <ClockIcon className="h-[3rem] w-[3rem]" />,
              text: 'Featured for 72 hrs',
            },
            {
              icon: <TrendUpIcon className="h-[3rem] w-[3rem]" />,
              text: '2x bump to top of search list',
            },
            {
              icon: <InstagramIcon className="h-[3rem] w-[3rem]" />,
              text: '1x Instastory on MX Locker account',
            },
            {
              icon: <EmailIcon className="h-[3rem] w-[3rem]" />,
              text: "Included in our weekly 'Bikes for Sale' email",
            },
          ]}
          index={3}
          form={form}
          setPackagePrice={setPackagePrice}
        />
      </div>

      <div className="mt-16 w-full">
        {Boolean(form.getValues('bike_package')) && (
          <div className="mt-8 flex w-full flex-col items-center justify-between gap-x-32 sm:flex-row">
            <div className="w-full sm:w-[70rem]">
              <h1 className="text-[2.4rem] font-medium text-black">
                Buy this package
              </h1>

              <FormLabel
                value="Select payment"
                required
                className="mt-8 w-full !text-[1.8rem] !font-medium !text-zinc-700"
              >
                <Select
                  allowEmpty={false}
                  options={paymentOptions}
                  value={selectedPaymentMethodId ?? ''}
                  onChange={(e) => {
                    setSelectedPaymentMethodId(e.value);
                  }}
                />
              </FormLabel>

              <div className="mt-8">
                <button
                  className="flex items-center gap-x-4 border-none bg-inherit"
                  type="button"
                  onClick={() => setOpenModal('payment')}
                >
                  <PlusCircleIcon className="h-10 w-10 text-brand-secondary" />
                  <span className="text-[1.8rem] text-brand-secondary">
                    Add new payment
                  </span>
                  <AddPaymentMethodModal
                    isOpen={openModal === 'payment'}
                    dismiss={(paymentMethodId?: string) => {
                      setOpenModal('');
                      if (paymentMethodId) {
                        setSelectedPaymentMethodId(paymentMethodId);
                      }
                    }}
                    isFullScreen={isMobile()}
                  />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-y-4 whitespace-nowrap">
              <h3 className="text-[1.8rem] font-medium text-zinc-700">
                Total purchase
              </h3>

              <h3 className="text-[2.4rem] font-semibold text-black">
                {packagePrice === 0 ? 'Free' : formatCurrency(packagePrice)}
              </h3>
            </div>
          </div>
        )}
      </div>
    </PageSection>
  );
}

const PackageCard = ({
  isPopular = false,
  price,
  description,
  items,
  index,
  form,
  setPackagePrice,
}: {
  isPopular?: boolean;
  price: number;
  description: string;
  items: Array<{ icon: React.ReactNode; text: string }>;
  index: number;
  form: any;
  setPackagePrice: (price: number) => void;
}) => {
  return (
    <div
      className={`cursor-pointer rounded-2xl border ${
        form.watch('bike_package') === index
          ? 'border-brand-secondary'
          : 'border-brand-lightest-gray'
      } bg-white p-8 hover:border-brand-secondary`}
      onClick={() => {
        form.setValue('bike_package', index);
        setPackagePrice(price);
      }}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-x-4">
          {price === 0 ? (
            <h1 className="text-[2.8rem] font-semibold text-black">Free</h1>
          ) : (
            <h1 className="text-[2.8rem] font-semibold text-black">
              {formatCurrency(price)}
            </h1>
          )}

          <h3 className="mt-2 text-[2.2rem] font-medium text-zinc-700">
            {description}
          </h3>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex flex-col gap-y-6">
          {items.map((i, _) => (
            <div className="flex items-center gap-x-8" key={_}>
              {i.icon}

              <span className="text-[1.7rem] font-medium text-zinc-600">
                {i.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
